export interface PlanningPeriod {
  period_id: number
  period_start: string
  period_end: string
  period_len_days: number
}

const periodMonthKey = (period: PlanningPeriod): string => {
  const start = Date.parse(`${period.period_start}T00:00:00Z`)
  const end = Date.parse(`${period.period_end}T00:00:00Z`)
  const midpoint = new Date((start + end) / 2)
  return `${midpoint.getUTCFullYear()}-${String(midpoint.getUTCMonth() + 1).padStart(2, '0')}`
}

export const getCurrentWeekNumber = (period: PlanningPeriod, today: string): number => {
  if (today < period.period_start) return 0
  const weekCount = Math.ceil(period.period_len_days / 7)
  if (today > period.period_end) return weekCount

  const start = Date.parse(`${period.period_start}T00:00:00Z`)
  const currentDate = Date.parse(`${today}T00:00:00Z`)
  const elapsedDays = Math.floor((currentDate - start) / 86400000)
  return Math.min(weekCount, Math.floor(elapsedDays / 7) + 1)
}

export const resolveWeeklyPlanPeriods = (
  periods: PlanningPeriod[],
  today: string,
  requestedSelectedMonth: string | null,
): { selectedPeriod: PlanningPeriod | null; navigationAnchorPeriod: PlanningPeriod | null } => {
  if (periods.length === 0) {
    return { selectedPeriod: null, navigationAnchorPeriod: null }
  }

  const calendarCurrentPeriod = periods.find(
    (period) => period.period_start <= today && today <= period.period_end,
  ) ?? null
  const lastPeriodBeforeToday = [...periods]
    .reverse()
    .find((period) => period.period_end < today) ?? null
  const fallbackPeriod = calendarCurrentPeriod ?? lastPeriodBeforeToday ?? periods[periods.length - 1]

  const requestedPeriod = /^\d{4}-\d{2}$/.test(requestedSelectedMonth ?? '')
    ? periods.find((period) => periodMonthKey(period) === requestedSelectedMonth) ?? null
    : null

  return {
    selectedPeriod: requestedPeriod ?? fallbackPeriod,
    // При разрыве календаря используем тот же безопасный fallback, чтобы граница
    // навигации не исчезала полностью. В нормальном календаре это всегда текущий период.
    navigationAnchorPeriod: calendarCurrentPeriod ?? fallbackPeriod,
  }
}

export const getMaxSelectablePeriodIndex = (
  periods: PlanningPeriod[],
  navigationAnchorPeriod: PlanningPeriod | null,
  today: string,
): number => {
  if (!navigationAnchorPeriod) return -1
  const anchorIndex = periods.findIndex(
    (period) => period.period_id === navigationAnchorPeriod.period_id,
  )
  if (anchorIndex < 0) return -1

  const canOpenNextPeriod = getCurrentWeekNumber(navigationAnchorPeriod, today) >= 4
  return Math.min(periods.length - 1, anchorIndex + (canOpenNextPeriod ? 1 : 0))
}
